import { useForm, Controller } from "react-hook-form";
import { z } from "zod/v3";
import { zodV3Resolver as zodResolver } from "@/lib/zodV3Resolver";
import { useMutation } from "@apollo/client";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import Select from "@/components/Select";
import { CREATE_JOB_APPLICATION_REVIEW, UPDATE_JOB_APPLICATION_REVIEW } from "../queries";
import TextField from "@/components/TextField/TextField";
import { useEffect } from "react";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import classNames from "classnames";

const RATING_OPTIONS = [
  { value: "1", label: "1 (Poor fit / do not move forward)" },
  { value: "2", label: "2 (Consider moving forward / investigate further)" },
  { value: "3", label: "3 (Great fit / move forward)" }
];

const RATING_OPTIONS_RECOMMENDATION = [
  { value: "1", label: "Decline" },
  { value: "2", label: "Consider" },
  { value: "3", label: "Advance" }
];

const schema = z.object({
  rating: z.enum(["1", "2", "3"], { required_error: "Rating is required" }),
  review: z.string().min(20, "Reviews require 20-1500 characters").max(1500, "Reviews require 20-1500 characters")
});

type FormValues = z.infer<typeof schema>;

export default function CreateApplicationReviewDialog({
  open,
  onClose,
  jobApplicationId,
  applicantName,
  isRecommendation,
  onSuccess,
  reviewToEdit
}: {
  open: boolean;
  onClose: () => void;
  jobApplicationId: string;
  applicantName: string;
  isRecommendation: boolean;
  onSuccess?: () => void;
  reviewToEdit?: any;
}) {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      rating: undefined,
      review: ""
    }
  });

  // Reset form when dialog opens/closes or reviewToEdit changes
  useEffect(() => {
    if (open) {
      if (reviewToEdit) {
        reset({
          rating: String(reviewToEdit.score) as "1" | "2" | "3",
          review: reviewToEdit.comments
        });
      } else {
        reset({
          rating: undefined,
          review: ""
        });
      }
    }
  }, [open, reviewToEdit, reset]);

  const [createReview, { loading: isCreatingReview }] = useMutation(CREATE_JOB_APPLICATION_REVIEW, {
    onCompleted: (data) => {
      if (data?.createJobApplicationReview?.success) {
        reset({
          rating: undefined,
          review: ""
        });
        showSnackbar(isRecommendation ? "Recommendation saved" : "Review saved", { close: true });
        onSuccess?.();
        onClose();
      } else {
        const message = data?.createJobApplicationReview?.message;
        const headline = isRecommendation ? "Recommendation not saved" : "Review not saved";
        if (message) {
          showErrorSnackbar(headline, { subtitle: message });
        } else {
          showErrorSnackbar(headline, { subtitle: "Please try again later." });
        }
      }
    },
    onError: () => {
      showErrorSnackbar(isRecommendation ? "Recommendation not saved" : "Review not saved", {
        subtitle: "Please try again later."
      });
    }
  });

  const [updateReview, { loading: isUpdatingReview }] = useMutation(UPDATE_JOB_APPLICATION_REVIEW, {
    onCompleted: (data) => {
      if (data?.updateJobApplicationReview?.success) {
        reset({
          rating: undefined,
          review: ""
        });
        showSnackbar(isRecommendation ? "Recommendation saved" : "Review saved", { close: true });
        onSuccess?.();
        onClose();
      } else {
        const message = data?.updateJobApplicationReview?.message;
        const headline = isRecommendation ? "Recommendation not saved" : "Review not saved";
        if (message) {
          showErrorSnackbar(headline, { subtitle: message });
        } else {
          showErrorSnackbar(headline, { subtitle: "Please try again later." });
        }
      }
    },
    onError: () => {
      showErrorSnackbar(isRecommendation ? "Recommendation not saved" : "Review not saved", {
        subtitle: "Please try again later."
      });
    }
  });

  const onSubmit = (values: FormValues) => {
    const input = {
      score: Number(values.rating),
      comments: values.review,
      jobApplicationId: jobApplicationId,
      ...(reviewToEdit?.id && { id: reviewToEdit.id }),
      isRecommendation
    };

    if (reviewToEdit) {
      updateReview({
        variables: { input }
      });
    } else {
      createReview({
        variables: {
          input: {
            ...input,
            isRecommendation
          }
        }
      });
    }
  };

  const handleClose = () => {
    reset({
      rating: undefined,
      review: ""
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline={
        isRecommendation
          ? reviewToEdit
            ? `Edit Recommendation for ${applicantName}`
            : `Add Recommendation for ${applicantName}`
          : `Add Review for ${applicantName}`
      }
      className="w-[520px] p-6"
      displayOverflow
      actions={
        <div className="flex gap-2">
          <Button variant="text" onClick={handleClose} disabled={isSubmitting || isCreatingReview || isUpdatingReview}>
            Cancel
          </Button>
          <Button
            variant="filled"
            iconClassName={classNames(
              (isSubmitting || isCreatingReview || isUpdatingReview) && "animate-spin w-6 h-6 !block"
            )}
            icon={isSubmitting || isCreatingReview || isUpdatingReview ? "progress_activity" : undefined}
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting || isCreatingReview || isUpdatingReview}
          >
            {reviewToEdit ? "Update" : "Save"}
          </Button>
        </div>
      }
    >
      <div>
        <p className="mb-4">
          {isRecommendation
            ? "Provide a rating and a few sentences about this applicant's job fit or your previous experience with them.   "
            : "Provide a few sentences about why you are recommending this applicant be advanced to, considered, or declined for the next job application phase."}
        </p>
        <div className="flex flex-col gap-6">
          <Controller
            name="rating"
            control={control}
            render={({ field }) => (
              <Select
                label={isRecommendation ? "Recommendation" : "Rating"}
                value={field.value}
                onSelect={field.onChange}
                options={isRecommendation ? RATING_OPTIONS_RECOMMENDATION : RATING_OPTIONS}
                required
                error={!!errors.rating}
                errorText={errors.rating?.message}
              />
            )}
          />
          <Controller
            name="review"
            control={control}
            render={({ field }) => (
              <TextField
                className="w-full"
                label={isRecommendation ? "Comments" : "Review"}
                variant="outlined"
                value={field.value}
                onChange={field.onChange}
                required
                type="textarea"
                error={!!errors.review}
                errorText={errors.review?.message}
                rows={4}
              />
            )}
          />
        </div>
      </div>
    </Dialog>
  );
}
